import React from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Award, Gift, Trophy } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const BestHub = () => {
  const categories = [
    {
      icon: <Award className="h-8 w-8 text-primary" />,
      title: "Employee Recognition Software",
      description: "Discover the best platforms to celebrate achievements and build recognition culture.",
      link: "/best/employee-recognition-software"
    },
    {
      icon: <Trophy className="h-8 w-8 text-primary" />,
      title: "Employee Rewards Platforms",
      description: "Compare top rewards platforms that motivate teams and drive engagement.",
      link: "/best/employee-rewards-platforms"
    },
    {
      icon: <Gift className="h-8 w-8 text-primary" />,
      title: "Employee Gifting Platforms",
      description: "Find the best gifting solutions to show appreciation and celebrate milestones.",
      link: "/best/gifting-platforms"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Best Employee Recognition & Rewards Software | Assembly</title>
        <meta name="description" content="Discover the best employee recognition software, rewards platforms, and gifting tools. Compare top solutions to boost engagement and reward contributions." />
        <meta name="keywords" content="best employee recognition software, employee rewards platforms, gifting tools, compare recognition software" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Navbar />
        
        <main className="pt-24 pb-16">
          {/* Hero Section */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-foreground bg-clip-text text-transparent">
                Discover the Best Employee Recognition & Rewards Software
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                Find the perfect platform to boost engagement, celebrate achievements, and build a thriving workplace culture.
              </p>
            </div>
            
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full text-primary font-medium">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                Updated Annually
              </div>
            </div>
          </section>

          {/* Categories Grid */}
          <section className="px-6 max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {categories.map((category, index) => (
                <Card key={index} className="group hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                  <CardHeader className="text-center pb-4">
                    <div className="flex justify-center mb-4">
                      {category.icon}
                    </div>
                    <CardTitle className="text-xl mb-2">{category.title}</CardTitle>
                    <CardDescription className="text-base">
                      {category.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Button 
                      asChild 
                      className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                      variant="outline"
                    >
                      <a href={category.link} className="flex items-center justify-center gap-2">
                        See Full List
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Trust Section */}
          <section className="px-6 max-w-7xl mx-auto mt-16">
            <div className="text-center bg-muted/30 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4">Why Trust Our Rankings?</h2>
              <div className="grid md:grid-cols-3 gap-6 text-sm text-muted-foreground">
                <div>
                  <div className="font-semibold text-foreground mb-2">Comprehensive Research</div>
                  <p>We analyze features, pricing, user reviews, and market presence to provide unbiased rankings.</p>
                </div>
                <div>
                  <div className="font-semibold text-foreground mb-2">Annual Updates</div>
                  <p>Our rankings are refreshed annually to reflect the latest product developments and market changes.</p>
                </div>
                <div>
                  <div className="font-semibold text-foreground mb-2">Industry Expertise</div>
                  <p>Built by HR technology experts who understand what makes employee recognition programs successful.</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BestHub;